import { computed, Injectable, signal } from '@angular/core'
import { LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { AccessLevel } from '@ftr/distribution-contracts'
import { LocalDate } from '@js-joda/core'

// TODO - Delete this file once cart state has been implemented
@Injectable({ providedIn: 'root' })
export class FakeCart {
  readonly items = signal<FakeCartItem[]>(initialItems)

  readonly groupedItems = computed(() => this.listItemsInGroups())

  listItems(): FakeCartItem[] {
    return this.items()
  }

  listItemsInGroups(): FakeCartGroup[] {
    const groupsMap = new Map<string, FakeCartGroup>()

    this.items().forEach(item => {
      const groupKey = `${item.location.id}-${item.date.toString()}`

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, {
          key: groupKey,
          location: item.location,
          date: item.date,
          items: [],
        })
      }

      groupsMap.get(groupKey)!.items.push({
        id: item.id,
        department: item.department,
        availableTime: item.availableTime,
        requestedTime: item.requestedTime,
        accessLevel: item.accessLevel,
      })
    })

    const groups = Array.from(groupsMap.values())
    groups.forEach(group => {
      group.items.sort((a, b) => a.requestedTime.start.compareTo(b.requestedTime.start))
    })

    return groups
  }

  addItem(item: FakeCartItem): void {
    this.items.update(items => [...items, item])
  }

  removeItem(id: Uuid): void {
    this.items.update(items => items.filter(item => item.id !== id))
  }

  clear(): void {
    this.items.set([])
  }
}

export interface FakeCartItem {
  id: Uuid
  location: { id: Uuid; name: string; parentId: Uuid; parentName: string }
  department: { id: Uuid; name: string }
  date: LocalDate
  availableTime: LocalTimeRange
  requestedTime: LocalTimeRange
  accessLevel: AccessLevel
}

export interface FakeCartGroup {
  key: string
  location: FakeCartItem['location']
  date: LocalDate
  items: Omit<FakeCartItem, 'location' | 'date'>[]
}

const location1 = {
  id: 'courtroom-1',
  name: 'District Courtroom 2',
  parentId: 'courthouse-1',
  parentName: 'Brockton Trial Courthouse',
}
const location2 = {
  id: 'courtroom-2',
  name: 'District Courtroom 1',
  parentId: 'courthouse-1',
  parentName: 'Brockton Trial Courthouse',
}
const location3 = {
  id: 'courtroom-3',
  name: 'Courtroom 4',
  parentId: 'courthouse-2',
  parentName: 'Ayer District Courthouse',
}
const department1 = { id: 'department-1', name: 'Department 1' }
const department2 = { id: 'department-2', name: 'Department 2' }

const initialItems: FakeCartItem[] = [
  {
    id: 'item-1',
    location: location1,
    department: department1,
    date: LocalDate.of(2025, 10, 24),
    availableTime: LocalTimeRange.fromSecondOfDay(46800, 48000),
    requestedTime: LocalTimeRange.fromSecondOfDay(46800, 48000),
    accessLevel: AccessLevel.PerApproval,
  },
  {
    id: 'item-2',
    location: location2,
    department: department1,
    date: LocalDate.of(2025, 10, 24),
    availableTime: LocalTimeRange.fromSecondOfDay(32400, 34500),
    requestedTime: LocalTimeRange.fromSecondOfDay(32400, 34500),
    accessLevel: AccessLevel.Public,
  },
  {
    id: 'item-3',
    location: location2,
    department: department2,
    date: LocalDate.of(2025, 10, 24),
    availableTime: LocalTimeRange.fromSecondOfDay(36720, 37920),
    requestedTime: LocalTimeRange.fromSecondOfDay(36720, 37920),
    accessLevel: AccessLevel.Public,
  },
  {
    id: 'item-4',
    location: location3,
    department: department1,
    date: LocalDate.of(2025, 9, 20),
    availableTime: LocalTimeRange.fromSecondOfDay(32400, 33600),
    requestedTime: LocalTimeRange.fromSecondOfDay(32400, 33600),
    accessLevel: AccessLevel.PerApproval,
  },
  {
    id: 'item-5',
    location: location3,
    department: department1,
    date: LocalDate.of(2025, 9, 20),
    availableTime: LocalTimeRange.fromSecondOfDay(36000, 38700),
    requestedTime: LocalTimeRange.fromSecondOfDay(36000, 38700),
    accessLevel: AccessLevel.PerApproval,
  },
  {
    id: 'item-6',
    location: location3,
    department: department1,
    date: LocalDate.of(2025, 9, 20),
    availableTime: LocalTimeRange.fromSecondOfDay(39000, 40800),
    requestedTime: LocalTimeRange.fromSecondOfDay(39000, 40800),
    accessLevel: AccessLevel.PerApproval,
  },
]
